import queryString from 'query-string'
import moment from 'moment'
import env from '../config/env'
import { Loading } from '../components/common/Loading'
import { authService } from '../services/auth.service'
import { createRoot } from 'react-dom/client'

// Flag để theo dõi xem đã clear session data chưa
let hasSessionBeenCleared = false;

// Hàm clear session storage khi reload trang
const clearSessionDataOnReload = () => {
  if (!hasSessionBeenCleared) {
    // Clear toàn bộ session storage
    sessionStorage.clear();
    hasSessionBeenCleared = true;
  }
};

// Gọi hàm clear toàn bộ session khi khởi động ứng dụng
clearSessionDataOnReload();

const GlobalApi: any = {
  userID: '',
  userCombination: '',
  pushToken: '',
  // Loại bỏ userToken, deviceId, fcmToken khỏi GlobalApi, tất cả sẽ được lấy trực tiếp từ localStorage
  apiUrl: env.API_URL,
  refreshingToken: false,
  overlayView: null
}

// Tạo loading container ở global scope
let loadingContainer: HTMLDivElement | null = null;
let loadingRoot: any = null;

// Hàm hiển thị loading
const showLoading = () => {
  // Không cần clear session ở đây nữa vì đã được gọi khi khởi động
  
  if (!loadingContainer) {
    loadingContainer = document.createElement('div');
    loadingContainer.id = 'api-loading-container';
    document.body.appendChild(loadingContainer);
    loadingRoot = createRoot(loadingContainer);
  }
  loadingRoot?.render(<Loading />);
};

// Hàm ẩn loading
const hideLoading = () => {
  if (loadingContainer && loadingRoot) {
    loadingRoot.unmount();
    loadingContainer.remove();
    loadingContainer = null;
    loadingRoot = null;
  }
};

// Single refresh token promise to ensure only one refresh happens at a time
let tokenRefreshPromise: Promise<any> | null = null;
// Flag to track if a refresh has completed recently
let tokenRefreshedRecently = false;
// Time window in milliseconds to consider a refresh as "recent" (5 seconds)
const REFRESH_WINDOW = 5000;
// Flag to detect if the page is being loaded/reloaded
const isPageReload = document.readyState !== 'complete';

/**
 * Tạo cache key từ thông tin request
 * @param method HTTP method (get, post, etc.)
 * @param path URL endpoint
 * @param params Request parameters
 * @returns A unique cache key for the request
 */
const createCacheKey = (method: string, path: string, params: any): string => {
  // Tạo cache key từ kết hợp method, path và params được stringify
  return `API_CACHE:${method.toUpperCase()}:${path}:${JSON.stringify(params)}`;
};

/**
 * Lấy data từ session storage cache
 * @param cacheKey Key để lấy data từ cache
 * @returns Cached data hoặc null nếu không có cache
 */
const getFromCache = (cacheKey: string): any => {
  const cachedData = sessionStorage.getItem(cacheKey);
  if (!cachedData) return null;

  try {
    const { data, expiry } = JSON.parse(cachedData);
    
    // Kiểm tra cache có hết hạn không
    if (expiry && new Date().getTime() > expiry) {
      // Cache đã hết hạn, xóa cache
      sessionStorage.removeItem(cacheKey);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Error parsing cached data:', error);
    sessionStorage.removeItem(cacheKey);
    return null;
  }
};

/**
 * Lưu data vào session storage cache
 * @param cacheKey Key để lưu data vào cache
 * @param data Data cần lưu vào cache
 * @param expireInMinutes Thời gian hết hạn của cache (phút)
 */
const saveToCache = (cacheKey: string, data: any, expireInMinutes: number = 5): void => {
  try {
    console.log(`[API Cache] Saving data to cache with key: ${cacheKey}`);
    
    // Tính thời gian hết hạn
    const expiry = expireInMinutes > 0 
      ? new Date().getTime() + (expireInMinutes * 60 * 1000)
      : null;
    
    // Lưu data và thời gian hết hạn vào cache
    const cacheData = JSON.stringify({
      data,
      expiry
    });
    
    sessionStorage.setItem(cacheKey, cacheData);
    console.log(`[API Cache] Data saved successfully, sessionStorage now has ${sessionStorage.length} items`);
  } catch (error) {
    console.error('Error saving data to cache:', error);
    // Nếu lỗi khi lưu cache, xóa cache cũ nếu có
    sessionStorage.removeItem(cacheKey);
  }
};

/**
 * Danh sách các API được phép sử dụng cache (whitelist)
 * Chỉ những API nằm trong danh sách này mới được cache
 */
const CACHEABLE_APIS = [
  // Thêm các API paths được phép cache vào đây
  // 'api/Links',
  'api/MoneyDeposit/GetBankAccList',
  // 'api/Market/StockList',
  // 'api/Market/MarketIndex'
  // Thêm các API khác vào đây nếu cần
];

/**
 * Kiểm tra xem một API endpoint có được phép cache không
 * @param path URL endpoint của API
 * @returns true nếu API được phép cache, false nếu không
 */
const isApiCacheable = (path: string): boolean => {
  // Kiểm tra nếu API path có trong whitelist
  return CACHEABLE_APIS.some(apiPath => path.includes(apiPath));
};

/**
 * Fetch API với khả năng cache kết quả vào session storage (chỉ cho APIs trong whitelist)
 * @param method HTTP method (get, post, etc.)
 * @param path URL endpoint
 * @param params Request parameters
 * @param paramMethod Method để xử lý params (json hoặc query)
 * @param cacheOptions Tùy chọn cache
 * @returns Promise với kết quả từ API hoặc cache
 */
const fetchApi = async (
  method: string, 
  path: string, 
  params = {}, 
  paramMethod = 'json',
  cacheOptions?: { 
    useCache?: boolean;        // Có sử dụng cache hay không, mặc định là false
    skipCache?: boolean;       // Có bỏ qua cache và force request mới hay không, mặc định là false
    expireInMinutes?: number;  // Thời gian cache hết hạn tính bằng phút, mặc định là 5 phút
  }
): Promise<any> => {
  // Default cache options
  const { 
    useCache = false, 
    skipCache = false, 
    expireInMinutes = 5 
  } = cacheOptions || {};

  // Chỉ hiện loading cho các API quan trọng
  // Không hiện loading cho:
  // - Refresh token tự động
  // - Initial auth check (loadStoredAuth)
  // - API Links
  const isTokenRefresh = path.includes('token') && path !== 'token/revoke';
  const isInitialAuthCheck = path.includes('api/Links');
  const shouldShowLoading = !isTokenRefresh
    && !isInitialAuthCheck && !path.includes('GetStartupOTP') && !path.includes('VerifyDeviceSmartOTP');;

  try {
    // Tạo cache key
    const cacheKey = createCacheKey(method, path, params);
    
    // Kiểm tra xem API có trong whitelist được phép cache không
    const isCacheAllowed = isApiCacheable(path);

    // Chỉ sử dụng cache nếu API nằm trong whitelist được phép cache
    if (isCacheAllowed && useCache && !skipCache) {
      // Chỉ cache các request GET
      const cachedData = getFromCache(cacheKey);
      if (cachedData !== undefined && cachedData !== null) {
        console.log(`[API Cache] Using cached data for whitelisted API: ${path}`);
        return cachedData;
      }
    }

    // Chỉ hiện loading nếu không phải là API đặc biệt
    if (shouldShowLoading) {
      showLoading();
    }
    
    // Lấy token trực tiếp từ localStorage thay vì GlobalApi
    const storedTokenStr = localStorage.getItem('@USER_TOKEN');
    if (!storedTokenStr) {
      return false;
    }
    
    // Parse token và kiểm tra tính hợp lệ
    let tokenData;
    try {
      tokenData = JSON.parse(storedTokenStr);
      if (!tokenData || !tokenData.access_token) {
        return false;
      }
    } catch (error) {
      console.error('Error parsing token:', error);
      return false;
    }
    
    // Kiểm tra token hết hạn
    if (tokenData['.expires']) {
      const tokenExpires = moment(tokenData['.expires']);
      const now = moment();
      const tokenExpired = now.isAfter(tokenExpires);
      const tokenNearExpiry = now.add(5, 'minutes').isAfter(tokenExpires);

      // Kiểm tra nếu token đã được refresh gần đây
      if (tokenRefreshedRecently) {
        console.log('Token was refreshed recently, no need to refresh again');
        // Không cần làm gì, tiếp tục dùng token hiện tại
      }
      // Chỉ refresh token nếu:
      // 1. Token đã hết hạn hoặc sắp hết hạn
      // 2. Chưa refresh gần đây
      // 3. Hiện tại KHÔNG có refresh đang được thực hiện bởi request khác
      else if ((tokenExpired || tokenNearExpiry) && !tokenRefreshPromise) {
        // Tạo refresh promise mới vì chưa có process nào đang refresh
        console.log('Creating new token refresh promise');
        tokenRefreshPromise = (async () => {
          try {
            console.log('Attempting to refresh token...');
            const rRes = await fetchApiLogin('post', 'token', { 
              grant_type: 'refresh_token', 
              refresh_token: tokenData.refresh_token 
            });
            
            if (rRes && rRes.access_token) {
              console.log('Token refreshed successfully');
              // Cập nhật localStorage với token mới
              await localStorage.setItem('@USER_TOKEN', JSON.stringify(rRes));
              
              // Đánh dấu đã refresh token gần đây để tránh refresh lại
              tokenRefreshedRecently = true;
              setTimeout(() => {
                tokenRefreshedRecently = false;
              }, REFRESH_WINDOW);
              
              return rRes;
            } else {
              throw new Error('Invalid refresh token response');
            }
          } catch (error: any) {
            console.error('Refresh token failed:', error);
            
            // Kiểm tra lỗi invalid_grant và force logout
            // Lấy response data từ error object (nếu có)
            const errorData = error.response;
            if (errorData && errorData.error === 'invalid_grant') {
              console.log('Invalid grant error detected in refresh token, forcing logout');
              authService.setForceLogoutFlag();
              authService.logout();
            }
            // Nếu token đã hết hạn và không thể refresh, logout
            else if (tokenExpired) {
              await localStorage.removeItem('@USER_TOKEN');
              authService.setForceLogoutFlag();
              authService.logout();
            }
            throw error;
          } finally {
            // Luôn reset refresh promise khi kết thúc
            tokenRefreshPromise = null;
          }
        })();
        
        try {
          // Chờ kết quả từ promise refresh token và cập nhật tokenData
          tokenData = await tokenRefreshPromise;
        } catch (error) {
          // Nếu refresh token thất bại, trả về false
          return false;
        }
      } else if (tokenRefreshPromise) {
        // Nếu một request khác đang refresh token, CHỜ cho đến khi refresh hoàn tất
        console.log('Another request is refreshing token, waiting for it to complete...');
        try {
          // Đợi cho refresh token process hiện tại hoàn thành và sử dụng token mới
          tokenData = await tokenRefreshPromise;
          console.log('Token refresh completed by another request, continuing with new token');
        } catch (error) {
          console.error('Error waiting for token refresh:', error);
          return false;
        }
      }
    }

    let finalPath = path;
    let options: any = {
      method: method.toUpperCase(),
      headers: {
        'Content-Type': paramMethod === 'json' ? 'application/json' : 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${tokenData.access_token}`,
        'X-App-Type': 'app',
        'Access-Control-Allow-Origin': '*'
      }
    };

    if (method.toUpperCase() === 'GET') {
      if (Object.entries(params).length > 0) {
        finalPath += '?' + Object.entries(params).map((v) => {
          if (Array.isArray(v[1])) {
            return `${v[0]}=${v[1].join(',')}`
          } else {
            return `${v[0]}=${v[1]}`
          }
        }).join('&')
      }
    } else {
      options['body'] = paramMethod === 'json' ? JSON.stringify(params) : queryString.stringify(params)
    }

    try {
      const response = await fetch(GlobalApi.apiUrl + `/${finalPath}`, options);
      
      // Handle 403 errors (Forbidden - typically authentication issues)
      if (response.status === 403) {
        // First check if response message contains "vui lòng login lại" and force logout
        try {
          const responseData = await response.clone().json();
          if (responseData && responseData.Message && responseData.Message.toLowerCase().includes("vui lòng login lại")) {
            console.log('Force logout due to 403 response message: ', responseData.Message);
            authService.setForceLogoutFlag();
            authService.logout();
            return false;
          }
        } catch (error) {
          console.error('Error parsing 403 response:', error);
        }
        
        // Nếu đã refresh token gần đây, thử lại request mà không refresh lại
        if (tokenRefreshedRecently) {
          console.log('Token was refreshed recently, retrying request without refreshing token again');
          return fetchApi(method, path, params, paramMethod);
        }
        
        // Thử refresh token trước khi logout
        // Lấy token mới nhất trực tiếp từ localStorage 
        const currentTokenStr = localStorage.getItem('@USER_TOKEN');
        if (!currentTokenStr) {
          authService.setForceLogoutFlag();
          authService.logout();
          return false;
        }
        
        try {
          const currentToken = JSON.parse(currentTokenStr);
          const tokenExpires = moment(currentToken['.expires']);
          const tokenExpired = moment().isAfter(tokenExpires);

          // Nếu token hết hạn và chưa refresh gần đây
          // Và không có request nào khác đang refresh token
          if (tokenExpired && !tokenRefreshedRecently && !tokenRefreshPromise) {
            // Tạo promise refresh token mới
            tokenRefreshPromise = (async () => {
              try {
                console.log('Attempting to refresh token on 403 error...');
                const rRes = await fetchApiLogin('post', 'token', { 
                  grant_type: 'refresh_token', 
                  refresh_token: currentToken.refresh_token 
                });
                
                if (rRes && rRes.access_token) {
                  console.log('Token refreshed successfully on 403');
                  await localStorage.setItem('@USER_TOKEN', JSON.stringify(rRes));
                  
                  // Đánh dấu đã refresh token gần đây
                  tokenRefreshedRecently = true;
                  setTimeout(() => {
                    tokenRefreshedRecently = false;
                  }, REFRESH_WINDOW);
                  
                  return rRes;
                } else {
                  throw new Error('Invalid refresh token response on 403');
                }
              } catch (error: any) {
                console.error('Refresh token failed on 403:', error);
                
                // Kiểm tra lỗi invalid_grant và force logout
                // Lấy response data từ error object (nếu có)
                const errorData = error.response;
                if (errorData && errorData.error === 'invalid_grant') {
                  console.log('Invalid grant error detected in refresh token, forcing logout');
                  authService.setForceLogoutFlag();
                  authService.logout();
                }
                
                throw error;
              } finally {
                // Reset promise when done
                tokenRefreshPromise = null;
              }
            })();
          }
          
          try {
            // Chờ kết quả refresh token
            if (tokenRefreshPromise) {
              await tokenRefreshPromise;
            }
            // Nếu refresh thành công, thử lại request ban đầu
            return fetchApi(method, path, params, paramMethod);
          } catch (error) {
            console.error('Waiting for token refresh failed:', error);
            return false;
          }
        } catch (error) {
          console.error('Error parsing token in 403 handler:', error);
        }

        // Nếu refresh token thất bại hoặc không thể refresh
        authService.setForceLogoutFlag();
        authService.logout();
        return false;
      } else if (tokenRefreshPromise) {
        // Nếu một request khác đang refresh token, CHỜ cho đến khi refresh hoàn tất
        console.log('Another request is refreshing token on 403, waiting for it to complete...');
        try {
          // Đợi cho refresh token process hiện tại hoàn thành và sử dụng token mới
          tokenData = await tokenRefreshPromise;
          console.log('Token refresh completed by another request on 403, continuing with new token');
          // Thử lại request với token mới
          return fetchApi(method, path, params, paramMethod);
        } catch (error) {
          console.error('Waiting for token refresh failed:', error);
          authService.setForceLogoutFlag();
          authService.logout();
          return false;
        }
      }

      const data = await response.json();

      // Check if response contains "vui lòng login lại" in the Message field and force logout
      if (data && data.Message && data.Message.toLowerCase().includes("vui lòng login lại")) {
        console.log('Force logout due to server response message: ', data.Message);
        authService.setForceLogoutFlag();
        authService.logout();
        return false;
      }
      
      // On successful response - cache the result ONLY if API is whitelisted and caching is enabled
      const isCacheAllowed = isApiCacheable(path);
      if (isCacheAllowed && useCache) {
        console.log('[API Cache] Checking if response can be cached:', {
          method: method.toUpperCase(),
          path,
          dataType: typeof data,
          hasData: !!data,
          status: data?.Status
        });
        
        // Cache any valid response for both GET and POST requests
        if (data !== undefined && data !== null) {
          saveToCache(cacheKey, data, expireInMinutes);
        }
      }

      return data;
    } catch (err) {
      console.error('API Error:', { err, path, params });
      return false;
    }
  } catch (error) {
    console.log({ error });
    
    // Force logout if API error occurs during page reload
    if (isPageReload || document.readyState !== 'complete') {
      console.error('API error during page load/reload, forcing logout');
      authService.logout();
    }
    
    return false;
  } finally {
    if (shouldShowLoading) hideLoading();
  }
};

const fetchApiSignUp = async (method: string, path: string, params = {}): Promise<any> => {
  const shouldShowLoading = true; // Sign up luôn cần hiện loading
  try {
    // Hiển thị loading khi đang sign up
    if (shouldShowLoading) showLoading();
    let finalPath = path
    let options: any = {
      method: method.toUpperCase(),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-App-Type': 'app'
      }
    }

    if (method.toUpperCase() === 'GET') {
      finalPath += '?' + Object.entries(params).map((v) => {
        if (Array.isArray(v[1])) {
          return `${v[0]}=${v[1].join(',')}`
        } else {
          return `${v[0]}=${v[1]}`
        }
      }).join('&')
    } else {
      options['body'] = queryString.stringify(params)
    }

    return await fetch(GlobalApi.apiUrl + `/${finalPath}`, options).then(res => {
      return res.json()
    }).then(response => {
      return response
    }).catch(err => {
      console.info("__err__", err)
    })
  } finally {
    if (shouldShowLoading) hideLoading();
  }
}

const fetchApiLogin = async (method: string, path: string, params = {}, paramMethod = 'query'): Promise<any> => {
  // Chỉ hiện loading cho login/register và revoke token
  // Không hiện loading cho refresh token tự động
  const isLoginOrRegister = (params as any).grant_type === 'password';
  const isTokenRevoke = path === 'token/revoke';
  const shouldShowLoading = isLoginOrRegister || isTokenRevoke;

  try {
    // Hiển thị loading khi đang login
    if (shouldShowLoading) showLoading();
    let finalPath = path;
    let options: any = {
      method: method.toUpperCase(),
      headers: {
        'Content-Type': paramMethod === 'query' ? 'application/x-www-form-urlencoded' : 'application/json',
        'X-App-Type': 'app',
        'Access-Control-Allow-Origin': '*'
      },
    };
    
    // Lấy token từ localStorage nếu có
    const storedTokenStr = localStorage.getItem('@USER_TOKEN');
    if (storedTokenStr) {
      try {
        const tokenData = JSON.parse(storedTokenStr);
        if (tokenData && tokenData.access_token) {
          options.headers['Authorization'] = `Bearer ${tokenData.access_token}`;
        }
      } catch (error) {
        console.error('Error parsing token in fetchApiLogin:', error);
      }
    }

    if (method.toUpperCase() === 'GET') {
      finalPath += '?' + Object.entries(params).map((v) => {
        if (Array.isArray(v[1])) {
          return `${v[0]}=${v[1].join(',')}`;
        } else {
          return `${v[0]}=${v[1]}`;
        }
      }).join('&');
    } else {
      options['body'] = (paramMethod === 'query' ? queryString.stringify(params) : JSON.stringify(params));
    }

    console.log({ url: GlobalApi.apiUrl + `/${finalPath}`, options });

    const response = await fetch(GlobalApi.apiUrl + `/${finalPath}`, options);
    const data = await response.json();

    if (!response.ok) {
      // Create an error object that contains both the original data and description
      const errorObj = new Error(data?.error_description || data?.Message || 'Login failed');
      (errorObj as any).response = data; // Attach the response data for access later
      throw errorObj;
    }

    return data;
  } catch (error) {
    console.error('Login error:', error);
    throw error; // Throw error để xử lý ở component
  } finally {
    if (shouldShowLoading) hideLoading();
  }
}

const fetchUploadApi = async (method: string, path: string, params = {}): Promise<any> => {
  const shouldShowLoading = true; // Upload API luôn cần hiện loading
  try {
    // Hiển thị loading khi đang upload
    if (shouldShowLoading) showLoading();
    
    // Lấy token trực tiếp từ localStorage
    const storedTokenStr = localStorage.getItem('@USER_TOKEN');
    if (!storedTokenStr) {
      console.error('No token found for upload API');
      return false;
    }
    
    // Parse token và kiểm tra tính hợp lệ
    let tokenData;
    try {
      tokenData = JSON.parse(storedTokenStr);
      if (!tokenData || !tokenData.access_token) {
        console.error('Invalid token format for upload API');
        return false;
      }
    } catch (error) {
      console.error('Error parsing token for upload API:', error);
      return false;
    }
    
    let finalPath = path

    var body = new FormData();

    Object.entries(params).map((v: any) => {
      body.append(v[0], v[1]);
    })

    let options = {
      method: method.toUpperCase(),
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${tokenData.access_token}`,
        'X-App-Type': 'app',
        'Access-Control-Allow-Origin': '*'
      },
      body
    }

    return await fetch(GlobalApi.apiUrl + `/${finalPath}`, options).then(res => {
      return res.json()
    }).then(response => {
      return response
    }).catch(err => {
      console.info("__err__", err)
    })
  } finally {
    if (shouldShowLoading) hideLoading();
  }
}

export {
  GlobalApi,
  fetchApi,
  fetchApiLogin,
  fetchApiSignUp,
  fetchUploadApi
}